import { Link, useLocation } from 'react-router-dom';
import { Phone, MessageCircle, FileText } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  PHONE_NUMBER_LINK,
  PHONE_NUMBER_DISPLAY,
  WHATSAPP_LINK,
  WHATSAPP_NUMBER_DISPLAY,
} from '@/constants/contact';
import { posthog } from '@/lib/posthog';
import { trackCtaClick } from '@/lib/ctaTracking';

/**
 * Fixed mobile-only bottom action bar: WhatsApp / Call / Quote.
 * Hidden on desktop and on admin routes.
 */
const MobileActionBar = () => {
  const { language } = useLanguage();
  const { pathname } = useLocation();

  if (pathname.startsWith('/admin') || pathname.startsWith('/branding') || pathname === '/install') {
    return null;
  }

  const isFr = language === 'fr';
  const lang = isFr ? 'fr' : 'en';

  const track = (channel: string) => {
    try {
      posthog.capture?.('mobile_action_bar_click', { channel, path: pathname });
    } catch { /* ignore */ }
    if (channel === 'whatsapp') {
      trackCtaClick('whatsapp', 'mobile_action_bar', { path: pathname, language: lang });
    } else if (channel === 'call') {
      trackCtaClick('call', 'mobile_action_bar', { path: pathname, language: lang });
    }
  };

  const itemClass =
    'flex flex-col items-center justify-center gap-0.5 flex-1 min-h-14 py-2 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background';

  return (
    <nav
      aria-label={isFr ? 'Actions rapides' : 'Quick actions'}
      className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.15)]"
      role="navigation"
    >
      <ul className="flex items-stretch">
        <li className="flex-1">
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('whatsapp')}
            aria-label={`WhatsApp ${WHATSAPP_NUMBER_DISPLAY}`}
            className={`${itemClass} text-[hsl(142,76%,32%)]`}
          >
            <MessageCircle className="w-5 h-5" aria-hidden="true" />
            <span>WhatsApp</span>
          </a>
        </li>
        <li className="flex-1 border-x border-border">
          <a
            href={PHONE_NUMBER_LINK}
            onClick={() => track('call')}
            aria-label={`${isFr ? 'Appeler' : 'Call'} ${PHONE_NUMBER_DISPLAY}`}
            className={`${itemClass} text-primary`}
          >
            <Phone className="w-5 h-5" aria-hidden="true" />
            <span>{isFr ? 'Appeler' : 'Call'}</span>
          </a>
        </li>
        <li className="flex-1">
          <Link
            to="/quote-request"
            onClick={() => track('quote')}
            aria-label={isFr ? 'Demander un devis' : 'Request a quote'}
            className={`${itemClass} text-accent`}
          >
            <FileText className="w-5 h-5" aria-hidden="true" />
            <span>{isFr ? 'Devis' : 'Quote'}</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default MobileActionBar;
